import Button from "@material-ui/core/Button";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";
import React from "react";
import "./FadeIn.css";

const useStyles = makeStyles((theme) => ({
  projectsContainer: {
    backgroundColor: "#0A0A0A",
    width: "100%",
    paddingBottom: "100px",
    paddingTop: "100px",
  },
  sectionTitle: {
    color: "#FFFFFF",
    fontWeight: 700,
    marginBottom: "60px",
    position: "relative",
    display: "inline-block",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: "-12px",
      left: 0,
      width: "60px",
      height: "4px",
      background: "#00FF41",
      borderRadius: "2px",
    },
  },
  projectCard: {
    background: "rgba(20, 20, 20, 0.8)",
    backdropFilter: "blur(10px)",
    border: "1px solid #2A2A2A",
    borderRadius: "16px",
    padding: "32px",
    marginBottom: "40px",
    transition: "all 0.3s ease",
    "&:hover": {
      boxShadow: "0 20px 40px rgba(0, 255, 65, 0.1)",
      borderColor: "#00FF41",
    },
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "24px",
  },
  logoBox: {
    background: "#FFFFFF",
    borderRadius: "12px",
    padding: "20px 30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  projectLogo: {
    maxWidth: "200px",
    maxHeight: "60px",
    objectFit: "contain",
  },
  projectLogoWithBorder: {
    maxWidth: "200px",
    maxHeight: "60px",
    objectFit: "contain",
  },
  projectDescription: {
    color: "#E0E0E0",
    fontSize: "0.95rem",
    lineHeight: 1.8,
    marginBottom: "16px",
  },
  techLabel: {
    color: "#00FF41",
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: "8px",
    marginTop: "16px",
  },
  techList: {
    color: "#B0B0B0",
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },
  buttonContainer: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
    flexWrap: "wrap",
  },
  primaryButton: {
    background: "#00FF41",
    color: "#000000",
    borderRadius: "8px",
    padding: "10px 20px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    transition: "all 0.3s ease",
    "&:hover": {
      background: "#00CC33",
      transform: "translateY(-2px)",
      boxShadow: "0 10px 20px rgba(0, 255, 65, 0.3)",
    },
  },
  secondaryButton: {
    background: "transparent",
    color: "#FFFFFF",
    borderRadius: "8px",
    padding: "10px 20px",
    fontWeight: 600,
    textTransform: "none",
    fontSize: "0.9rem",
    border: "1px solid #2A2A2A",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      backgroundColor: "rgba(0, 255, 65, 0.1)",
      transform: "translateY(-2px)",
    },
  },
  link: {
    textDecoration: "none",
  },
  inlineLink: {
    color: "#00CC33",
    textDecoration: "none",
    fontWeight: 600,
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#00FF41",
    },
  },
  websitePreview: {
    borderRadius: "12px",
    border: "1px solid #2A2A2A",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
    maxHeight: "400px",
    width: "100%",
    objectFit: "cover",
    objectPosition: "top",
    transition: "all 0.3s ease",
    "&:hover": {
      borderColor: "#00FF41",
      transform: "scale(1.02)",
    },
  },
  previewContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      marginTop: "24px",
    },
  },
  repoLinks: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    marginTop: "8px",
  },
}));

const projects = [
  {
    logo: "/images/projectPics/dlzp.png",
    logoStyle: "normal",
    logoWidth: "250px",
    siteUrl: "https://www.dlzpgroup.com/",
    screenshot: "/images/projectPics/dlzp-page.png",
    descriptions: [
      "Designed and deployed an AI chatbot using RAG (Retrieval-Augmented Generation) connected to client company information and customer data, with a vector search system of client database information using Python, AWS Lambda functions, S3 Vector Buckets, dynamic system prompts, and AWS Bedrock.",
      "Developed responsive Angular frontends for a variety of components while building much of the backend functionality: AI chatbot interface, fully dynamic Kanban board application enabling users to create, assign, and move tasks across workflow stages with real-time updates.",
      "Architected serverless backend logic using AWS Lambda, DynamoDB, API Gateway, and CI/CD pipelines (CodePipeline).",
    ],
    technologies: "Angular, Python, AWS Lambda, AWS Bedrock, DynamoDB, S3, API Gateway, RAG, Vector Search, CI/CD, CodePipeline",
  },
  {
    logo: "/images/projectPics/soltrade.png",
    logoStyle: "border",
    logoWidth: "200px",
    siteUrl: "https://www.linkedin.com/company/soltrade-systems-llc",
    screenshot: "/images/projectPics/SolTrade-website.png",
    descriptions: [
      "Founded SolTrade Systems, a proprietary trading technology company specializing in automated cryptocurrency arbitrage from social media. I developed a trading platform using Node.js, Python, and blockchain APIs to execute profitable trades on the Solana network, generating about $4,000 monthly profit.",
      "I also developed a system that detects notification delays on platforms like Patreon, enabling rapid arbitrage opportunities across cryptocurrency tokens, leveraged positions, and stock options. The company has demonstrated proven profitability with over $40,000 in realized gains so far through its automated trading platform.",
    ],
    technologies: "Node.js, Python, Solana Blockchain, Jupiter/Helius APIs",
  },
  {
    logo: "/images/projectPics/taxrise-logo.png",
    logoStyle: "normal",
    logoWidth: "250px",
    siteUrl: "https://taxrise.com/",
    screenshot: "/images/projectPics/taxrise.png",
    descriptions: [
      "I built several React components, Node/Python REST API routes, and controller code to interact with Postgres and MySQL Databases that support web applications currently used by TaxRise Employees and Clients. I taught myself how to develop on the Salesforce Platform through Trailheads and tutorials and have 1 full year of professional Salesforce Development experience.",
      "Created products such as: a Full-Stack SERN application that enables TaxRise Tax Associates to view/add/edit/delete financial records uploaded by our clients, Python backend code and React UI that enables TaxRise staff to create questions to dynamically appear on clients' tax questionnaires, and a Salesforce LWC UI and Apex code to enable clients to add/edit/delete their claimed tax assets.",
    ],
    technologies: "React, Node, Express, Python, Postgres, MySQL, FastAPI, CSS, Ant Design, AWS, Bitbucket, Agile, Jira",
    additionalTech: {
      label: "Salesforce Technologies",
      value: "Gearset, Apex, Apex Testing, LWCs, Aura Components, SOQL, Flow Builder, Schema Design, Object Design",
    },
  },
  {
    logo: "/images/projectPics/safety-audit-prep-logo.png",
    logoStyle: "normal",
    logoWidth: "250px",
    siteUrl: "https://www.safetyauditprep.net/",
    screenshot: "/images/projectPics/safety-audit-prep.png",
    descriptions: [
      "SafetyAuditPrep.com is an online CRM site built to manage DOT regulations for trucking companies. The site functions as a tool for Employee & Fleet Management, Drug & Alcohol Consortium, Compliance/Registration Management, and other utilities.",
      "As the Lead Full-Stack Developer on this project, I built the manager's homepage dashboard, the UI and necessary backend logic for managers to assign tractor/trailer units to drivers, the functionality for managers to provide privileges for employees and other functionalities. I also integrated the following REST & SOAP APIs: Quest (drug testing), TazWorks, FMCSA, and Authorize.NET.",
    ],
    technologies: "React, Redux, Node.JS, Express, Mongoose, MongoDB, Ant Design, Figma, AWS S3, REST & SOAP API Integration",
    buttons: [
      { label: "Visit Site", url: "https://www.safetyauditprep.net/" },
      { label: "View My Work", url: "https://my-safetyauditprep-components.netlify.app/" },
    ],
  },
  {
    logo: "/images/projectPics/freebay-logo.png",
    logoStyle: "normal",
    logoWidth: "250px",
    siteUrl: "https://freebay.netlify.app/",
    screenshot: "/images/projectPics/freebay.png",
    descriptions: [
      "FreeBay is a fake product bidding site that allows users to bid on fake products. Users can search and browse for fake products that are up for auction and compete with other users by placing bids. Each user can view other bidder's profiles to view the products they have won or have the highest bid on.",
      "Product data was web scraped from various Amazon product sites using Python then hosted in an API I created. This API utilizes search query functionality to find products, supplies a JSON web token upon user validation, and provides most recent wins/products.",
    ],
    technologies: "Node.JS, Postgres, Node-PG, Express, React, Material-UI, Axios, Python, Web-Scraping",
    repos: [
      { label: "Front End", url: "https://github.com/Bolmstead/Freebay" },
      { label: "API & Back End", url: "https://github.com/Bolmstead/Freebay-API" },
      { label: "Angular Version", url: "https://github.com/Bolmstead/DLZP-app" },
    ],
    buttons: [
      { label: "Visit React Site", url: "https://freebay.netlify.app/" },
      { label: "Visit Angular Site", url: "https://dlzp-berkley.netlify.app/" },
    ],
  },
];

export default function Projects() {
  const classes = useStyles();

  return (
    <Grid item xs={12} className={classes.projectsContainer}>
      <span id="Experience" style={{ position: "absolute", marginTop: "-80px" }}></span>
      <Container maxWidth="lg">
        <Typography variant="h4" className={classes.sectionTitle}>
          Professional Experience
        </Typography>

        {projects.map((project, index) => (
          <div className={classes.projectCard} key={index}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <div className={classes.logoContainer}>
                  <a
                    href={project.siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className={classes.logoBox}>
                      <img
                        src={project.logo}
                        className={project.logoStyle === "border" ? classes.projectLogoWithBorder : classes.projectLogo}
                        style={{ maxWidth: project.logoWidth }}
                        alt="Company Logo"
                      />
                    </div>
                  </a>
                </div>

                {project.descriptions.map((desc, i) => (
                  <Typography className={classes.projectDescription} key={i}>
                    {desc}
                  </Typography>
                ))}

                <Typography className={classes.techLabel}>
                  Technologies
                </Typography>
                <Typography className={classes.techList}>
                  {project.technologies}
                </Typography>

                {project.additionalTech && (
                  <>
                    <Typography className={classes.techLabel}>
                      {project.additionalTech.label}
                    </Typography>
                    <Typography className={classes.techList}>
                      {project.additionalTech.value}
                    </Typography>
                  </>
                )}

                {project.repos && (
                  <>
                    <Typography className={classes.techLabel}>
                      Repositories
                    </Typography>
                    <div className={classes.repoLinks}>
                      {project.repos.map((repo, i) => (
                        <span key={i}>
                          <a
                            href={repo.url}
                            className={classes.inlineLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {repo.label}
                          </a>
                          {i < project.repos.length - 1 && (
                            <span style={{ color: "#707070", margin: "0 8px" }}>/</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </>
                )}

                <div className={classes.buttonContainer}>
                  {project.buttons ? (
                    project.buttons.map((btn, i) => (
                      <a
                        href={btn.url}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={i}
                      >
                        <Button
                          className={i === 0 ? classes.primaryButton : classes.secondaryButton}
                          startIcon={<LaunchIcon />}
                        >
                          {btn.label}
                        </Button>
                      </a>
                    ))
                  ) : (
                    <a
                      href={project.siteUrl}
                      className={classes.link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button
                        className={classes.primaryButton}
                        startIcon={<LaunchIcon />}
                      >
                        Visit Site
                      </Button>
                    </a>
                  )}
                </div>
              </Grid>

              <Grid item xs={12} md={6} className={classes.previewContainer}>
                <a
                  href={project.siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={project.screenshot}
                    className={classes.websitePreview}
                    alt="Website Preview"
                  />
                </a>
              </Grid>
            </Grid>
          </div>
        ))}
      </Container>
    </Grid>
  );
}
